import React from 'react';

import {
  Accordion,
  ContentGroup,
  Heading,
  Link,
  List,
  ListItem,
  Main,
  Paragraph,
} from '@constellation/core';
import { useContent } from '@interstellar/react-app-content';
import { StyledLink } from 'client/components/styledLink/StyledLink';
import { Helmet } from 'react-helmet';

import { RentIncreasesContent } from './RentIncreasesPage.config';
import * as routes from '../manifest';

function RentIncreasesPage() {
  const {
    backLabel,
    metaContent,
    title,
    description,
    periodicTenancy,
    fixedTermTenancy,
    rentIncreaseRequirements,
    disagreement,
    walesAccordion,
    scotlandAccordion,
    northernIrelandAccordion,
  } = useContent<RentIncreasesContent>();
  return (
    <Main>
      <Helmet>
        <title>{metaContent.title}</title>
      </Helmet>
      <ContentGroup marginTop="05" marginBottom="none">
        <StyledLink
          iconPosition="left"
          data-testid="back-button-link"
          to={routes.LandlordSupport}
        >
          {backLabel}
        </StyledLink>
      </ContentGroup>

      <Heading as="h1" size="s7" marginTop="05">
        {title}
      </Heading>
      <Paragraph>{description}</Paragraph>

      <Heading as="h2" size="s3">
        {periodicTenancy.title}
      </Heading>
      <Paragraph>{periodicTenancy.description}</Paragraph>

      <Heading as="h2" size="s3">
        {fixedTermTenancy.title}
      </Heading>
      <Paragraph>{fixedTermTenancy.includedACondition}</Paragraph>
      <Paragraph>{fixedTermTenancy.realisticRentIncrease}</Paragraph>
      <Heading as="h3" size="s2">
        {rentIncreaseRequirements.youMust}
      </Heading>
      <List>
        <ListItem>{rentIncreaseRequirements.listItem1}</ListItem>
        <ListItem>{rentIncreaseRequirements.listItem2}</ListItem>
        <ListItem>
          {rentIncreaseRequirements.complete}
          <Link
            data-testid="new-rent-notice-link"
            href={rentIncreaseRequirements.formLinkHref}
            external
          >
            {rentIncreaseRequirements.formLinkLabel}
          </Link>
          {rentIncreaseRequirements.period}
        </ListItem>
      </List>
      <Heading as="h3" size="s3">
        {disagreement.title}
      </Heading>
      <Paragraph>
        {disagreement.haveAConversation}
        <Link
          data-testid="gov-tribunal-link"
          href={disagreement.tribunalLinkHref}
          external
        >
          {disagreement.tribunalLinkLabel}
        </Link>
        {disagreement.decideRightAmount}
      </Paragraph>

      <Accordion
        label={walesAccordion.label}
        variation="standalone"
        data-testid="wales-accordion"
      >
        <Paragraph>
          {walesAccordion.needAComplete}
          <Link
            data-testid="wales-rent-variation-link"
            href={walesAccordion.noticeOfVariationLinkHref}
            external
          >
            {walesAccordion.noticeOfVariationLinkLabel}
          </Link>
          {walesAccordion.postToTenants}
        </Paragraph>
        <Paragraph>{walesAccordion.fixedTermContract}</Paragraph>
        <Heading as="h3" size="s2">
          {walesAccordion.howMuchCanIIncreaseTitle}
        </Heading>
        <Paragraph marginBottom="none">{walesAccordion.noLegalLimit}</Paragraph>
      </Accordion>

      <Accordion
        data-testid="scotland-accordion"
        label={scotlandAccordion.label}
        variation="standalone"
        marginTop="05"
      >
        <Paragraph>
          {scotlandAccordion.privateTenancy}
          <Link
            data-testid="scotland-rent-increase-link"
            href={scotlandAccordion.rentIncreaseNoticeLinkHref}
            external
          >
            {scotlandAccordion.rentIncreaseNoticeLinkLabel}
          </Link>
          {scotlandAccordion.cantUseForOtherTenancy}
        </Paragraph>
        <Paragraph>{scotlandAccordion.jointTenancy}</Paragraph>
        <Paragraph>{scotlandAccordion.scottishGovernment}</Paragraph>
        <Paragraph marginBottom="none">
          {scotlandAccordion.challengeRentIncreases}
          <Link
            data-testid="scotland-rent-increase-referral-link"
            href={scotlandAccordion.rentServiceLinkHref}
            external
          >
            {scotlandAccordion.rentServiceLinkLabel}
          </Link>
          {scotlandAccordion.applyToRentService}
        </Paragraph>
      </Accordion>

      <Accordion
        label={northernIrelandAccordion.label}
        variation="standalone"
        marginTop="05"
      >
        <Heading as="h2" size="s2">
          {northernIrelandAccordion.fixedTermTitle}
        </Heading>
        <Paragraph>{northernIrelandAccordion.fixedTermDescription}</Paragraph>
        <Heading as="h2" size="s2">
          {northernIrelandAccordion.periodicTitle}
        </Heading>
        <Paragraph>{northernIrelandAccordion.periodicDescription}</Paragraph>
        <Heading as="h2" size="s2">
          {northernIrelandAccordion.rentControlledTitle}
        </Heading>
        <Paragraph>
          {northernIrelandAccordion.rentControlledDescription}
        </Paragraph>
        <Paragraph marginBottom="none">
          <Link
            data-testid="northern-ireland-rent-control-link"
            href={northernIrelandAccordion.rentControlLinkHref}
            external
          >
            {northernIrelandAccordion.rentControlLinkLabel}
          </Link>
        </Paragraph>
      </Accordion>
    </Main>
  );
}

export default RentIncreasesPage;
